/**
 * @file Submission UI — BackgroundDetailsReadonlySection.
 * Background Data on Dataset Overview — same UI as contributor BackgroundData form.
 */

import React, { useMemo } from "react";
import type { FormField } from "../../interfaces/formInterfaces";
import { useStateDropdown } from "../../hooks/useStateDropdown";
import { useGeographicScopeDropdown } from "../../hooks/useGeographicScopeDropdown";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/BackgroundData/BackgroundData.css";
import "./BackgroundDetailsReadonlySection.css";
import BackgroundFieldRenderer from "../../pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundFieldRenderer";
import BackgroundRawMaterials from "../../pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundRawMaterials";
import BackgroundDataReviewersBlock from "../../pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundDataReviewersBlock";
import BackgroundGeographicalSpecificityField from "../../pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundGeographicalSpecificityField";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
} from "../../utils/backgroundGeographicalSpecificity";
import {
  isDataReviewerTemplateFieldName,
  isExternalDataReviewSelection,
  normalizeDataReviewValue,
  requiredDataReviewerCount,
  buildDataReviewerGroups,
} from "../../pages/Dashboard/Contributor/Stages/BackgroundData/utils/backgroundDataReviewers";
import {
  BACKGROUND_PAIRED_GROUPS,
  backgroundDataCompletenessLabel,
  backgroundEnabledPairsFromValues,
  buildBackgroundReadonlyManufacturerOptions,
  buildBackgroundReadonlyMaterials,
  buildBackgroundReadonlyRawMaterials,
  buildBackgroundReadonlySubMaterials,
  buildBackgroundReadonlyTechnologyOptions,
  buildBackgroundReadonlyUnits,
  buildBackgroundReadonlyValues,
  resolveBackgroundStageFromSchema,
} from "./backgroundDetailsReadonly";
import { dataTypeFieldOptions } from "./backgroundDataType";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import { stripFieldInfoText } from "./readonlyFormUtils";
import type { AnyObj, ReferenceLabels } from "./readonlyFormDisplay";

const noopChange = () => {};
const noopMaterialSelect = () => {};
const noopFile = () => {};
const noopCheckbox = () => {};

type BackgroundDetailsReadonlySectionProps = {
  submission: AnyObj;
};

const BackgroundDetailsReadonlySection: React.FC<
  BackgroundDetailsReadonlySectionProps
> = ({ submission }) => {
  const { stateOptions } = useStateDropdown();
  const { geographicScopeOptions } = useGeographicScopeDropdown();
  const formSchema: AnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];
  const references: ReferenceLabels = (submission.referenceLabels ??
    {}) as ReferenceLabels;

  const background = (submission.background ?? null) as AnyObj | null;
  const rawMats = (submission.backgroundRawMats ??
    submission.backgroundRawMaterials ??
    []) as AnyObj[];
  const dataReviewers = (submission.backgroundDataReviewers ??
    []) as AnyObj[];

  const { orderedAll, fieldsBeforeRaw, fieldsAfterRaw, dataReviewerNameField, dataReviewerEmailField, dataTypeField } =
    resolveBackgroundStageFromSchema(formSchema);

  const dataTypeOptions = useMemo(
    () => dataTypeFieldOptions(dataTypeField),
    [dataTypeField]
  );

  const stringValues = useMemo(
    () => buildBackgroundReadonlyValues(background, rawMats, dataReviewers, dataTypeOptions),
    [background, rawMats, dataReviewers, dataTypeOptions]
  );

  const values = useMemo(
    () => stringValues as Record<string, string | File>,
    [stringValues]
  );

  const enabledPairs = useMemo(
    () => backgroundEnabledPairsFromValues(stringValues),
    [stringValues]
  );

  const pairedGroups = BACKGROUND_PAIRED_GROUPS;

  const materials = useMemo(
    () => buildBackgroundReadonlyMaterials(background, references),
    [background, references]
  );

  const subMaterials = useMemo(
    () => buildBackgroundReadonlySubMaterials(references),
    [references]
  );

  const rawMaterials = useMemo(
    () => buildBackgroundReadonlyRawMaterials(rawMats, references),
    [rawMats, references]
  );

  const allUnits = useMemo(
    () => buildBackgroundReadonlyUnits(references),
    [references]
  );

  const technologyOptions = useMemo(
    () => buildBackgroundReadonlyTechnologyOptions(background, references),
    [background, references]
  );

  const manufacturerOptions = useMemo(
    () => buildBackgroundReadonlyManufacturerOptions(background, references),
    [background, references]
  );

  const rawMaterialGroups = useMemo(() => {
    if (rawMats.length > 0) return rawMats.map((_, idx) => idx + 1);
    return [1];
  }, [rawMats]);

  const dataReviewFieldOptions = useMemo(() => {
    const dataReviewField = orderedAll.find((f) => f.name === "dataReview");
    return (
      dataReviewField?.dropdown?.options ??
      dataReviewField?.dropdownValues ??
      []
    );
  }, [orderedAll]);

  const normalizedDataReview = useMemo(
    () =>
      normalizeDataReviewValue(
        stringValues.dataReview ?? background?.dataReview,
        dataReviewFieldOptions
      ),
    [background?.dataReview, dataReviewFieldOptions, stringValues.dataReview]
  );

  const reviewerGroups = useMemo(() => {
    if (dataReviewers.length > 0) {
      return dataReviewers.map((_, idx) => idx + 1);
    }
    return buildDataReviewerGroups(
      requiredDataReviewerCount(normalizedDataReview, dataReviewFieldOptions)
    );
  }, [dataReviewFieldOptions, dataReviewers, normalizedDataReview]);

  const externalDataReviewSelected = isExternalDataReviewSelection(
    normalizedDataReview,
    dataReviewFieldOptions
  );

  const subMaterialUnitField = orderedAll.find((f) => f.name === "subMaterialUnit");
  const rawMaterialUnitTemplateField =
    orderedAll.find((f) => f.name === "rawMaterialUnit_01") ??
    orderedAll.find((f) => /^rawMaterialUnit_/.test(f.name ?? ""));
  const backgroundRawMaterialUnitPropertyId =
    rawMaterialUnitTemplateField?.unitPropertyId ??
    subMaterialUnitField?.unitPropertyId;

  const getDataCompletenessDisplay = () =>
    backgroundDataCompletenessLabel(stringValues);

  const fieldSequence: Array<
    { kind: "field"; field: FormField } | { kind: "raw" }
  > = [
    ...fieldsBeforeRaw.map((field) => ({ kind: "field" as const, field })),
    { kind: "raw" as const },
    ...fieldsAfterRaw.map((field) => ({ kind: "field" as const, field })),
  ];

  const subMaterialGroupFields = [
    "subMaterialId",
    "finishedQuantity",
    "subMaterialUnit",
  ];
  const startTimelineFields = [
    "collectionStartYear",
    "collectionStartMonth",
    "collectionStartDay",
  ];
  const endTimelineFields = [
    "collectionEndYear",
    "collectionEndMonth",
    "collectionEndDay",
  ];
  const hiddenPairedNumericFields = ["temperature"];

  const rendererProps = {
    values,
    materials,
    subMaterials,
    rawMaterials,
    allUnits,
    enabledPairs,
    pairedGroups,
    technologyOptions,
    manufacturerOptions,
    disabled: true as const,
    onMaterialSelect: noopMaterialSelect,
    onChange: noopChange,
    onFileChange: noopFile,
    onCheckboxChange: noopCheckbox,
  };

  const renderBgField = (f: FormField, extra?: Partial<FormField>) => (
    <BackgroundFieldRenderer
      field={stripFieldInfoText({ ...f, ...extra })}
      {...rendererProps}
    />
  );

  if (!background) {
    return (
      <DatasetOverviewFormShell
        variant="background"
        emptyMessage="No background data available."
      />
    );
  }

  return (
    <DatasetOverviewFormShell variant="background">
        {fieldSequence.map((item) => {
          if (item.kind === "raw") {
            return (
              <BackgroundRawMaterials
                key="raw-materials-section"
                rawMaterialGroups={rawMaterialGroups}
                values={values}
                rawMaterials={rawMaterials}
                allUnits={allUnits}
                unitPropertyId={backgroundRawMaterialUnitPropertyId}
                onAddGroup={noopMaterialSelect}
                onRemoveGroup={noopMaterialSelect}
                onChange={noopChange}
                readonly
              />
            );
          }

          const field = item.field;
          if (!field.isEnabled) return null;

          if (
            subMaterialGroupFields.includes(field.name) &&
            field.name !== "subMaterialId"
          ) {
            return null;
          }

          if (Object.values(pairedGroups).includes(field.name)) {
            return null;
          }

          if (
            [...startTimelineFields, ...endTimelineFields].includes(field.name) &&
            field.name !== "collectionStartYear" &&
            field.name !== "collectionEndYear"
          ) {
            return null;
          }

          if (hiddenPairedNumericFields.includes(field.name)) {
            return null;
          }

          if (field.name === "subMaterialId") {
            const f2 = orderedAll.find((f) => f.name === "finishedQuantity");
            const f3 = orderedAll.find((f) => f.name === "subMaterialUnit");

            return (
              <div key="subMaterial-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <div key={f.id} className="three-field-cell">
                        <label>
                          {f.label}
                          {f.isMandatory && <span className="required">*</span>}
                        </label>
                        {renderBgField(f)}
                      </div>
                    )
                )}
              </div>
            );
          }

          if (pairedGroups[field.name]) {
            const pairedField = orderedAll.find(
              (f) => f.name === pairedGroups[field.name]
            );

            if (pairedField) {
              const mainKey = field.name;
              return (
                <div key={`${field.name}-pair`} className="three-field-row">
                  <div className="three-field-cell">
                    <label>
                      Enable {field.label}
                      {field.isMandatory && <span className="required">*</span>}
                    </label>
                    <select
                      value={enabledPairs[mainKey] ? "yes" : "no"}
                      disabled
                      onChange={noopChange}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  {[field, pairedField].map((f) => (
                    <div key={f.id} className="three-field-cell">
                      <label>
                        {f.label}
                        {f.isMandatory && <span className="required">*</span>}
                      </label>
                        {renderBgField(f, {
                          isEditable: false,
                          isEnabled: false,
                        })}
                    </div>
                  ))}
                </div>
              );
            }
          }

          if (field.name === "collectionStartYear") {
            const f2 = orderedAll.find((f) => f.name === "collectionStartMonth");
            const f3 = orderedAll.find((f) => f.name === "collectionStartDay");

            return (
              <div key="timeline-start-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <div key={f.id} className="three-field-cell">
                        <label>
                          {f.label}
                          {f.isMandatory && <span className="required">*</span>}
                        </label>
                        {renderBgField(f, {
                          isEditable: false,
                          isEnabled: false,
                        })}
                      </div>
                    )
                )}
              </div>
            );
          }

          if (field.name === "collectionEndYear") {
            const f2 = orderedAll.find((f) => f.name === "collectionEndMonth");
            const f3 = orderedAll.find((f) => f.name === "collectionEndDay");

            return (
              <div key="timeline-end-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <div key={f.id} className="three-field-cell">
                        <label>
                          {f.label}
                          {f.isMandatory && <span className="required">*</span>}
                        </label>
                        {renderBgField(f, {
                          isEditable: false,
                          isEnabled: false,
                        })}
                      </div>
                    )
                )}
              </div>
            );
          }

          if (field.name === "a1" && field.type === "CHECKBOX") {
            const dataCompletenessField = orderedAll.find(
              (f) => f.name === "dataCompleteness"
            );
            const timelineField = orderedAll.find(
              (f) => f.name === "timelineAvailable"
            );
            const a2Field = orderedAll.find((f) => f.name === "a2");
            const a3Field = orderedAll.find((f) => f.name === "a3");
            const a1_a3Field = orderedAll.find((f) => f.name === "a1_a3");

            return (
              <div
                key="data-completeness-checkbox-timeline-group"
                className="background-full-width-group"
              >
                <div className="two-field-row data-completeness-geo-row">
                  <div className="two-field-row-inner data-completeness-geo-row-inner">
                    <div className="two-field-cell data-completeness-stack">
                      {dataCompletenessField && (
                        <div key={dataCompletenessField.id}>
                          <label>
                            {dataCompletenessField.label}
                            {dataCompletenessField.isMandatory && (
                              <span className="required">*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={getDataCompletenessDisplay()}
                            readOnly
                            className="readonly-input"
                            placeholder="Select A1, A2, A3, or A1-A3 below"
                          />
                        </div>
                      )}
                      <div key="checkbox-group" className="checkbox-group-row">
                        {[field, a2Field, a3Field, a1_a3Field].map(
                          (f) =>
                            f && (
                              <div key={f.id} className="checkbox-group-item">
                                {renderBgField(f)}
                              </div>
                            )
                        )}
                      </div>
                    </div>
                    <BackgroundGeographicalSpecificityField
                      cellClassName="two-field-cell"
                      geoSpec={String(values[BACKGROUND_GEO_SPEC_FIELD] ?? "global")}
                      geoSelection={String(values[BACKGROUND_GEO_SELECTION_FIELD] ?? "")}
                      stateOptions={stateOptions}
                      geographicScopeOptions={geographicScopeOptions}
                      readOnly
                    />
                  </div>
                </div>
                {timelineField && (
                  <div
                    key={timelineField.id}
                    className="field-row data-completeness-row"
                  >
                    <label>
                      {timelineField.label?.trim().endsWith("?")
                        ? timelineField.label
                        : `${(timelineField.label || "").trim()}?`}
                      {timelineField.isMandatory && (
                        <span className="required">*</span>
                      )}
                    </label>
                    {renderBgField(timelineField)}
                  </div>
                )}
              </div>
            );
          }

          if (["dataCompleteness", "timelineAvailable"].includes(field.name)) {
            return null;
          }

          if (
            ["a2", "a3", "a1_a3"].includes(field.name) &&
            field.type === "CHECKBOX"
          ) {
            return null;
          }

          if (isDataReviewerTemplateFieldName(field.name)) {
            return null;
          }

          if (field.name === "dataReview") {
            const dataAcquisitionField = orderedAll.find(
              (f) => f.name === "dataAcquisition"
            );

            return (
              <div
                key={`${field.id}-data-review-group`}
                className="field-row data-review-acquisition-group"
              >
                <div className="two-field-row-inner data-review-acquisition-row">
                  <div className="two-field-cell">
                    <label>
                      {field.label}
                      {field.isMandatory && <span className="required">*</span>}
                    </label>
                    {renderBgField(field)}
                  </div>
                  {dataAcquisitionField ? (
                    <div className="two-field-cell">
                      <label>
                        {dataAcquisitionField.label}
                        {dataAcquisitionField.isMandatory && (
                          <span className="required">*</span>
                        )}
                      </label>
                      {renderBgField(dataAcquisitionField)}
                    </div>
                  ) : null}
                </div>
                <BackgroundDataReviewersBlock
                  active={externalDataReviewSelected}
                  reviewerGroups={reviewerGroups}
                  values={values}
                  onChange={noopChange}
                  readonly
                  nameField={dataReviewerNameField}
                  emailField={dataReviewerEmailField}
                />
              </div>
            );
          }

          if (field.name === "dataAcquisition") {
            return null;
          }

          const isFullWidthField = field.name === "materialPropertiesAvailable";
          return (
            <div
              key={field.id}
              className={`field-row ${isFullWidthField ? "data-completeness-row" : ""}`}
            >
              <label>
                {field.label}
                {field.isMandatory && <span className="required">*</span>}
              </label>
              {renderBgField(field)}
            </div>
          );
        })}
    </DatasetOverviewFormShell>
  );
};

/** React component — Background Details Readonly Section. */
export default BackgroundDetailsReadonlySection;
